/**
 * Integrations Service Layer
 *
 * Calls real backend API endpoints for Companies, ProductTypes, ProductInstances.
 * Maps backend DTOs to frontend types (CompanyWithIntegrations etc.).
 * Same pattern as EBTimeEntry's integrationsApi.ts.
 */

import {
  companiesApi,
  productTypesApi,
  productInstancesApi,
  integrationsApi,
} from '@/services/api';
import type {
  CompanyDto,
  ProductInstanceDto,
  ExternalInstanceDto,
  AccessProjectsCompanyDto,
  AvailableDatabaseDto,
  CompanyUserDto,
} from '@/services/api';
import type {
  CompanyWithIntegrations,
  CompanyUser,
  EmployeeCompanyAccessRow,
  UpdateIntegrationsPayload,
  TestIntegrationResponse,
  SimpleIntegrationConfig,
  IntegrationConnectionStatus,
  FinancialProduct,
  HRProduct,
  ProjectProduct,
  PlannerProduct,
  TimesheetProduct,
} from '@/types/integrations';

// ---------------------------------------------------------------------------
// DTO → Frontend mapping helpers
// ---------------------------------------------------------------------------

function mapConnectionStatus(status: string): IntegrationConnectionStatus {
  switch (status) {
    case 'connected':
      return 'connected';
    case 'not_connected':
      return 'not_connected';
    case 'error':
      return 'error';
    case 'testing':
      return 'testing';
    default:
      return 'not_configured';
  }
}

/**
 * Matches a product instance's category against an integration type using
 * the same fuzzy-matching rules as EBTimeEntry's FindProductInstanceByCategoryAsync.
 * For example, "project" also matches "projects" so that products are
 * correctly resolved under the Project integration column.
 */
function matchesCategory(
  instanceCategory: string | null | undefined,
  integrationCategory: string
): boolean {
  const cat = (instanceCategory ?? '').toLowerCase();
  switch (integrationCategory) {
    case 'financial':
      return cat === 'financial' || cat === 'financials';
    case 'hr':
      return cat === 'hr' || cat === 'people';
    case 'project':
      return cat === 'project' || cat === 'projects';
    case 'planner':
      return cat === 'planner' || cat === 'scheduling' || cat === 'resource';
    case 'timesheet':
      return cat === 'timesheet' || cat === 'timesheets';
    default:
      return cat === integrationCategory;
  }
}

function buildIntegrationConfig(
  instances: ProductInstanceDto[],
  category: string
): SimpleIntegrationConfig {
  const instance = instances.find(
    (i) => matchesCategory(i.productTypeCategory, category)
  );
  if (!instance) {
    return {
      product: null,
      externalInstanceId: null,
      instanceName: null,
      status: 'not_configured',
      lastSyncAt: null,
    };
  }
  return {
    product: instance.productTypeCode as FinancialProduct | HRProduct | ProjectProduct | PlannerProduct | TimesheetProduct,
    externalInstanceId: instance.externalInstanceId ?? null,
    externalInstanceIdSecondary: instance.externalInstanceIdSecondary ?? null,
    instanceName: instance.instanceName ?? null,
    status: mapConnectionStatus(instance.connectionStatus),
    lastSyncAt: instance.lastSync ?? null,
  };
}

function mapCompanyDtoToCompanyWithIntegrations(
  dto: CompanyDto
): CompanyWithIntegrations {
  const instances = dto.productInstances ?? [];
  return {
    id: dto.id,
    name: dto.name,
    code: dto.code,
    status: dto.status === 'active' ? 'active' : 'inactive',
    financialIntegration: buildIntegrationConfig(instances, 'financial'),
    hrIntegration: buildIntegrationConfig(instances, 'hr'),
    projectIntegration: buildIntegrationConfig(instances, 'project'),
    plannerIntegration: buildIntegrationConfig(instances, 'planner'),
    timesheetIntegration: buildIntegrationConfig(instances, 'timesheet'),
    userCount: dto.userAccessCount,
  };
}

// ---------------------------------------------------------------------------
// Product type cache (loaded once for mapping code → id)
// ---------------------------------------------------------------------------

let productTypeCache: Array<{ id: string; code: string; category: string }> | null = null;

async function getProductTypeMap(): Promise<Array<{ id: string; code: string; category: string }>> {
  if (productTypeCache) return productTypeCache;
  const types = await productTypesApi.getAll();
  productTypeCache = types.map((t) => ({ id: t.id, code: t.code, category: t.category }));
  return productTypeCache;
}

/** Clear the in-memory product-type cache so the next call to
 *  getProductTypeMap() fetches fresh data from the API. */
export function clearProductTypeCache(): void {
  productTypeCache = null;
}

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

export async function getCompanies(): Promise<CompanyWithIntegrations[]> {
  const dtos = await companiesApi.getAll();
  return dtos.map(mapCompanyDtoToCompanyWithIntegrations);
}

export async function getMyCompanyAccess(): Promise<(string | { companyId: string; isDefault: boolean })[]> {
  return companiesApi.getMyAccess();
}

export async function getCompany(id: string): Promise<CompanyWithIntegrations | undefined> {
  try {
    const dto = await companiesApi.getById(id);
    return mapCompanyDtoToCompanyWithIntegrations(dto);
  } catch {
    return undefined;
  }
}

export async function createCompany(data: {
  name: string;
  code: string;
  status: 'active' | 'inactive';
}): Promise<CompanyWithIntegrations> {
  const dto = await companiesApi.create(data);
  return mapCompanyDtoToCompanyWithIntegrations(dto);
}

export async function updateCompany(
  id: string,
  data: Partial<Pick<CompanyWithIntegrations, 'name' | 'code' | 'status'>>
): Promise<CompanyWithIntegrations | undefined> {
  try {
    const dto = await companiesApi.update(id, data);
    return mapCompanyDtoToCompanyWithIntegrations(dto);
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Integrations (save product instances per category)
// ---------------------------------------------------------------------------

export async function updateIntegrations(
  companyId: string,
  payload: UpdateIntegrationsPayload
): Promise<CompanyWithIntegrations | undefined> {
  const types = await getProductTypeMap();

  const categories = [
    { key: 'financial' as const, field: payload.financial },
    { key: 'hr' as const, field: payload.hr },
    { key: 'project' as const, field: payload.project },
    { key: 'planner' as const, field: payload.planner },
    { key: 'timesheet' as const, field: payload.timesheet },
  ];
  const existingInstances = await productInstancesApi.getByCompany(companyId);

  for (const { key, field } of categories) {
    if (!field?.product) continue;

    const productType = types.find((t) => t.code === field.product);
    if (!productType) continue;

    const existing = existingInstances.find(
      (i) => matchesCategory(i.productTypeCategory, key)
    );

    const externalInstanceIdSecondary = 'externalInstanceIdSecondary' in field
      ? (field as { externalInstanceIdSecondary?: string | null }).externalInstanceIdSecondary
      : undefined;

    if (existing) {
      if (existing.productTypeCode !== field.product) {
        // Product changed within the same category — replace the instance
        await productInstancesApi.delete(companyId, existing.id);
        await productInstancesApi.create(companyId, {
          productType: productType.code,
          instanceName: field.instanceName ?? `${field.product} instance`,
          externalInstanceId: field.externalInstanceId ?? undefined,
          externalInstanceIdSecondary: externalInstanceIdSecondary ?? undefined,
        });
      } else {
        await productInstancesApi.update(companyId, existing.id, {
          instanceName: field.instanceName ?? existing.instanceName,
          externalInstanceId: field.externalInstanceId ?? undefined,
          externalInstanceIdSecondary: externalInstanceIdSecondary ?? undefined,
        });
      }
    } else {
      await productInstancesApi.create(companyId, {
        productType: productType.code,
        instanceName: field.instanceName ?? `${field.product} instance`,
        externalInstanceId: field.externalInstanceId ?? undefined,
        externalInstanceIdSecondary: externalInstanceIdSecondary ?? undefined,
      });
    }
  }

  return getCompany(companyId);
}

export async function syncIntegration(
  companyId: string,
  type: 'financial' | 'hr' | 'project' | 'planner' | 'timesheet'
): Promise<{ success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined }> {
  const result = await companiesApi.syncIntegration(companyId, type);
  const updatedCompany = result.updatedCompany
    ? mapCompanyDtoToCompanyWithIntegrations(result.updatedCompany)
    : await getCompany(companyId);
  return {
    success: result.success,
    message: result.message,
    syncedAt: result.syncedAt,
    updatedCompany,
  };
}

export async function resyncIntegration(
  companyId: string,
  type: 'financial' | 'hr' | 'project' | 'planner' | 'timesheet'
): Promise<{ success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined }> {
  const result = await companiesApi.resyncIntegration(companyId, type);
  const updatedCompany = result.updatedCompany
    ? mapCompanyDtoToCompanyWithIntegrations(result.updatedCompany)
    : await getCompany(companyId);
  return {
    success: result.success,
    message: result.message,
    syncedAt: result.syncedAt,
    updatedCompany,
  };
}

export async function removeIntegration(
  companyId: string,
  type: 'financial' | 'hr' | 'project' | 'planner' | 'timesheet'
): Promise<CompanyWithIntegrations | undefined> {
  const result = await companiesApi.removeIntegration(companyId, type);
  return result ? mapCompanyDtoToCompanyWithIntegrations(result) : await getCompany(companyId);
}

export async function testConnection(
  companyId: string,
  type: 'financial' | 'hr' | 'project' | 'planner' | 'timesheet'
): Promise<TestIntegrationResponse> {
  const result = await companiesApi.testIntegration(companyId, type);
  return {
    success: result.success,
    message: result.message,
    testedAt: result.testedAt,
  };
}

// ---------------------------------------------------------------------------
// External Instances (from Workspace API via backend)
// ---------------------------------------------------------------------------

export async function getExternalInstances(productCode: string, companyId?: string): Promise<ExternalInstanceDto[]> {
  return productTypesApi.getExternalInstances(productCode, companyId);
}

export async function getAccessProjectsCompanies(options?: { instanceId?: string; product?: string }): Promise<AccessProjectsCompanyDto[]> {
  return integrationsApi.getAccessProjectsCompanies(options);
}

export async function getAvailableDatabases(product: string): Promise<AvailableDatabaseDto[]> {
  return integrationsApi.getAvailableDatabases(product);
}

// ---------------------------------------------------------------------------
// API Secret (for webhook-based integrations like People HR)
// ---------------------------------------------------------------------------

export async function getApiSecret(
  companyId: string,
  type: 'hr' | 'project' | 'timesheet'
): Promise<{ maskedApiSecret: string | null }> {
  return companiesApi.getApiSecret(companyId, type);
}

export async function saveApiSecret(
  companyId: string,
  type: 'hr' | 'project' | 'timesheet',
  apiSecret: string
): Promise<{ maskedApiSecret: string | null }> {
  return companiesApi.saveApiSecret(companyId, type, apiSecret);
}

export async function deleteApiSecret(
  companyId: string,
  type: 'hr' | 'project' | 'timesheet'
): Promise<void> {
  return companiesApi.deleteApiSecret(companyId, type);
}

// ---------------------------------------------------------------------------
// Company Users (same pattern as EBProjects CompanyUserAccess)
// ---------------------------------------------------------------------------

function mapCompanyUserDtoToCompanyUser(dto: CompanyUserDto): CompanyUser {
  return {
    id: dto.id,
    name: dto.name,
    email: dto.email,
    role: dto.role,
    photoUrl: dto.photoUrl,
    isDefault: false,
  };
}

export async function getCompanyUsers(companyId: string): Promise<CompanyUser[]> {
  const dtos = await companiesApi.getUsers(companyId);
  return dtos.map(mapCompanyUserDtoToCompanyUser);
}

export async function addUserToCompany(
  companyId: string,
  userId: string
): Promise<CompanyUser | undefined> {
  try {
    const dto = await companiesApi.addUser(companyId, parseInt(userId, 10));
    return mapCompanyUserDtoToCompanyUser(dto);
  } catch {
    return undefined;
  }
}

export async function removeUserFromCompany(
  companyId: string,
  userId: string
): Promise<void> {
  await companiesApi.removeUser(companyId, parseInt(userId, 10));
}

export async function addUsersToCompany(
  companyId: string,
  userIds: string[]
): Promise<CompanyUser[]> {
  const resourceIds = userIds
    .map((id) => parseInt(id, 10))
    .filter((id) => !Number.isNaN(id));
  if (resourceIds.length === 0) return [];
  const dtos = await companiesApi.bulkAddUsers(companyId, resourceIds);
  return dtos.map(mapCompanyUserDtoToCompanyUser);
}

export async function removeUsersFromCompany(
  companyId: string,
  userIds: string[]
): Promise<void> {
  const resourceIds = userIds
    .map((id) => parseInt(id, 10))
    .filter((id) => !Number.isNaN(id));
  if (resourceIds.length === 0) return;
  await companiesApi.bulkRemoveUsers(companyId, resourceIds);
}

export async function searchUsers(
  query: string,
  excludeCompanyId?: string
): Promise<CompanyUser[]> {
  if (!excludeCompanyId) return [];
  const dtos = await companiesApi.searchUsers(excludeCompanyId, query);
  return dtos.map(mapCompanyUserDtoToCompanyUser);
}

export async function fetchAllUsers(excludeCompanyId: string): Promise<CompanyUser[]> {
  const dtos = await companiesApi.searchUsers(excludeCompanyId, '');
  return dtos.map(mapCompanyUserDtoToCompanyUser);
}

// ---------------------------------------------------------------------------
// Employee Company Access (backend not yet implemented — returns empty)
// ---------------------------------------------------------------------------

export async function getEmployeeCompanyAccess(
  _employeeId: string
): Promise<EmployeeCompanyAccessRow[]> {
  return [];
}

export async function updateEmployeeCompanyAccess(
  _employeeId: string,
  _updates: Array<{ companyId: string; hasAccess: boolean; isDefault: boolean }>
): Promise<EmployeeCompanyAccessRow[]> {
  return [];
}
